/**
 * useContactNoteAttachments
 * -----------------------------------------------------------------------------
 * Hook para gerenciar anexos de mídia de uma anotação de contato (chat).
 * Espelha `useDealAttachments` em termos de validação, upload e exclusão.
 *
 * Storage: bucket `anexos`, prefixo `contact-notes/{tenant_id}/{noteId}/`.
 * Tabela:  `contact_note_attachments`.
 *
 * @version 1.0.0
 * RELEASE: 2026-06-03 v1.0.0 - Criação inicial.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { logger } from '@/lib/logger';
import { sanitizeStorageFileName } from '@/lib/storageFileName';

// Tipos de arquivo aceitos no anexo de anotação.
export type ContactNoteAttachmentFileType =
  | 'image'
  | 'pdf'
  | 'document'
  | 'spreadsheet'
  | 'other';

export interface ContactNoteAttachment {
  id: string;
  tenant_id: string;
  note_id: string;
  file_name: string;
  file_type: ContactNoteAttachmentFileType;
  mime_type: string | null;
  file_size: number | null;
  storage_path: string;
  public_url: string;
  thumbnail_url: string | null;
  uploaded_by: string | null;
  created_at: string;
  updated_at: string;
}

// Limites (mesmos do CRM).
const MAX_FILE_SIZE = 10 * 1024 * 1024;
const MAX_ATTACHMENTS = 20;

const ALLOWED_MIME_TYPES: Record<string, ContactNoteAttachmentFileType> = {
  'image/jpeg': 'image',
  'image/jpg': 'image',
  'image/png': 'image',
  'image/gif': 'image',
  'image/webp': 'image',
  'application/pdf': 'pdf',
  'application/msword': 'document',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
    'document',
  'text/plain': 'document',
  'application/vnd.ms-excel': 'spreadsheet',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
    'spreadsheet',
};

function getFileType(mime: string): ContactNoteAttachmentFileType {
  return ALLOWED_MIME_TYPES[mime] || 'other';
}

function validateFile(file: File): { valid: boolean; error?: string } {
  if (file.size > MAX_FILE_SIZE) {
    return { valid: false, error: 'Arquivo muito grande. Máximo: 10MB' };
  }
  if (!ALLOWED_MIME_TYPES[file.type]) {
    return { valid: false, error: 'Tipo de arquivo não permitido' };
  }
  return { valid: true };
}

/** Remove acentos e caracteres especiais do nome do arquivo. */
// v1.x: delegado ao utilitário central (remove emojis/acentos e evita
// o erro "Invalid key" do Supabase Storage). O nome original permanece no banco.
function sanitizeFileName(fileName: string): string {
  return sanitizeStorageFileName(fileName);
}

/**
 * Hook principal — recebe o id da anotação (pode ser undefined quando ainda
 * não criada; nesse caso uploads ficam desabilitados).
 */
export function useContactNoteAttachments(noteId: string | undefined) {
  const { toast } = useToast();
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();

  // Listagem (SELECT direto na tabela; RLS garante isolamento por tenant).
  const attachmentsQuery = useQuery({
    queryKey: ['contact-note-attachments', noteId, currentTenant?.id],
    queryFn: async () => {
      if (!noteId) return [];
      const { data, error } = await supabase
        .from('contact_note_attachments')
        .select('*')
        .eq('note_id', noteId)
        .order('created_at', { ascending: true });
      if (error) {
        logger.error('[useContactNoteAttachments] list error', error.message);
        throw error;
      }
      return (data ?? []) as ContactNoteAttachment[];
    },
    enabled: !!noteId && !!currentTenant?.id,
  });

  // Upload de arquivo.
  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      if (!noteId || !currentTenant?.id) {
        throw new Error('Anotação ou tenant não definido');
      }
      const validation = validateFile(file);
      if (!validation.valid) throw new Error(validation.error);

      const currentCount = attachmentsQuery.data?.length || 0;
      if (currentCount >= MAX_ATTACHMENTS) {
        throw new Error(`Limite de ${MAX_ATTACHMENTS} anexos atingido`);
      }

      const fileType = getFileType(file.type);
      const sanitizedName = sanitizeFileName(file.name);
      const timestamp = Date.now();
      // Path: contact-notes/{tenant_id}/{noteId}/{timestamp}_{nome}
      const storagePath = `contact-notes/${currentTenant.id}/${noteId}/${timestamp}_${sanitizedName}`;

      const { error: uploadError } = await supabase.storage
        .from('anexos')
        .upload(storagePath, file, { cacheControl: '3600', upsert: false });
      if (uploadError) {
        logger.error('[useContactNoteAttachments] upload', uploadError.message);
        throw new Error('Falha no upload do arquivo');
      }

      const { data: urlData } = supabase.storage
        .from('anexos')
        .getPublicUrl(storagePath);

      const { data: user } = await supabase.auth.getUser();

      const { data: row, error: insertError } = await supabase
        .from('contact_note_attachments')
        .insert({
          tenant_id: currentTenant.id,
          note_id: noteId,
          file_name: sanitizedName,
          file_type: fileType,
          mime_type: file.type,
          file_size: file.size,
          storage_path: storagePath,
          public_url: urlData.publicUrl,
          thumbnail_url: fileType === 'image' ? urlData.publicUrl : null,
          uploaded_by: user.user?.id ?? null,
        })
        .select()
        .single();

      if (insertError) {
        // Limpa o arquivo se o insert falhar (consistência).
        await supabase.storage.from('anexos').remove([storagePath]);
        logger.error(
          '[useContactNoteAttachments] insert',
          insertError.message,
        );
        throw new Error('Falha ao registrar anexo');
      }

      return row as ContactNoteAttachment;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['contact-note-attachments', noteId],
      });
      toast({ title: 'Arquivo enviado' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Erro no upload',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Exclusão.
  const deleteMutation = useMutation({
    mutationFn: async (attachmentId: string) => {
      const { data: row, error: fetchErr } = await supabase
        .from('contact_note_attachments')
        .select('storage_path')
        .eq('id', attachmentId)
        .single();
      if (fetchErr || !row) throw new Error('Anexo não encontrado');

      const { error: rmErr } = await supabase.storage
        .from('anexos')
        .remove([row.storage_path]);
      if (rmErr) {
        logger.warn('[useContactNoteAttachments] storage remove', rmErr.message);
      }

      const { error: delErr } = await supabase
        .from('contact_note_attachments')
        .delete()
        .eq('id', attachmentId);
      if (delErr) throw new Error('Falha ao excluir anexo');

      return attachmentId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['contact-note-attachments', noteId],
      });
      toast({ title: 'Anexo removido' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Erro ao excluir',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  return {
    attachments: attachmentsQuery.data || [],
    isLoading: attachmentsQuery.isLoading,
    uploadFile: uploadMutation.mutateAsync,
    deleteAttachment: deleteMutation.mutate,
    isUploading: uploadMutation.isPending,
    isDeleting: deleteMutation.isPending,
    canAddMore: (attachmentsQuery.data?.length || 0) < MAX_ATTACHMENTS,
    maxAttachments: MAX_ATTACHMENTS,
    maxFileSize: MAX_FILE_SIZE,
  };
}
